import * as PropType from 'prop-types'
import { MenuItem, TextField } from '@material-ui/core'
import { Link } from 'react-router-dom'
import React, { useCallback, useEffect } from 'react'
import dayjs from 'dayjs'
import { useQuery } from '@tanstack/react-query'
import { noop } from 'lodash'
import { ADMIN_ROUTES, TEXT_VARIANTS } from '../../../../../constants'
import Text from '../../../../atoms/Text'
import Skeleton from '../../../../atoms/Skeleton'
import Tag from '../../../../atoms/Tag'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import { getBillingRuns } from '../../../../../service'
import { RUN_STATUS } from '../../billing/runBilling/helpers'
import { messageToUse } from '../../common'
import { cssVar } from '../../../../../utils/cssVar'

const BillingRunOverride = ({
  error = null,
  value = null,
  onChange = noop,
  classes = {}
}) => {
  const {
    data: billingRuns,
    isLoading: isLoadingBillingRuns
  } = useQuery({
    queryKey: [QUERY_KEYS.reports, 'billingRuns'],
    queryFn: async () => {
      const { data } = await getBillingRuns({
        take: 20,
        skip: 0,
        filters: {
          status: [
            { op: 'in', value: [RUN_STATUS.MARK_AS_FINAL, RUN_STATUS.IN_REVIEW] }
          ]
        },
        includes: { runBy: true },
        sort: [{ field: 'createdAt', dir: 'desc' }]
      })
      return data
    },
    enabled: true
  })

  useEffect(() => {
    if (billingRuns?.length && !value?.billingRunId) {
      onChange({ key: 'billingRun', value: billingRuns[0] })
    }
  }, [billingRuns, onChange, value?.billingRunId])

  const frequencyMap = {
    M: 'Monthly',
    Q: 'Quarterly',
    A: 'Annually'
  }
  const levelTypeMap = {
    CLIENT: {
      label: 'Client',
      backgroundColor: 'var(--color-surface-100)',
      color: 'var(--text-color-main)'
    },
    ACCOUNT: {
      label: 'Account',
      backgroundColor: 'var(--color-surface-200)',
      color: 'var(--text-color-main)'
    }
  }
  const runStatusMap = {
    IN_REVIEW: {
      label: 'In Review',
      backgroundColor: 'var(--color-info-500)',
      color: 'var(--color-info-050)'
    },
    MARK_AS_FINAL: {
      label: 'Marked as Final',
      backgroundColor: 'var(--color-primary-500)',
      color: 'var(--color-contrast-primary-600)'
    }
  }

  const handleOnChange = useCallback((e) => {
    const billingRunId = e.target.value
    onChange({
      key: 'billingRun',
      value: billingRuns.find(billingRun => billingRun.billingRunId === billingRunId)
    })
  }, [billingRuns, onChange])

  return (
    <div className={classes.groupItem}>
      <div className={classes.labelWithInfo}>
        <Text text='Billing Run to use' variant='h3' />
        <Link to={ADMIN_ROUTES.BILLING_RUN} target='_blank'>Manage</Link>
      </div>
      {
        isLoadingBillingRuns ? (
          <Skeleton
            className={classes.itemTextField}
            height='2.3rem'
            width='100%'
          />
        ) : !billingRuns?.length ? (
          <div style={{ marginTop: '.5rem' }}>
            <Text
              text='There are currently no finalized billing runs.  Use the manage button above to update your billing runs'
              variant='body2'
            />
          </div>
        ) : (
          <TextField
            select
            fullWidth
            className={classes.itemTextField}
            InputProps={{ disableUnderline: true }}
            SelectProps={{ MenuProps: { classes: { list: classes.taggedSelectMenu } } }}
            onChange={handleOnChange}
            error={Boolean(error?.length)}
            disabled={isLoadingBillingRuns}
            defaultValue={billingRuns[0].billingRunId}
            value={value?.billingRunId}
            style={{ padding: 0 }}
          >
            {billingRuns.map((
              {
                billingRunId, levelType, asOfDate, createdAt, frequency, runBy, status
              }) => {
              return (
                <MenuItem key={billingRunId} value={billingRunId} classes={{ root: classes.taggedSelectMenuItem }}>
                  <div className={classes.taggedSelectOption}>
                    <Text variant={TEXT_VARIANTS.body}>
                      <div>({frequencyMap[frequency]})</div>
                      <div><strong>As of:</strong> {asOfDate}</div>
                      <div><strong>Run Date:</strong> {dayjs(createdAt).format('M/D/YY h:mm a')}</div>
                      <div><strong>Run By:</strong> {runBy.firstName} {runBy.lastName}</div>
                    </Text>
                    <div className={classes.taggedSelectOptionTags}>
                      <Tag height='auto' {...levelTypeMap[levelType]} />
                      <Tag height='auto' {...runStatusMap[status]} />
                    </div>
                  </div>
                </MenuItem>
              )
            })}
          </TextField>
        )
      }

      {!!error?.length && (
        <Text
          text={messageToUse(error)}
          color={cssVar('--color-danger-500')}
          className={classes.confirmError}
        />
      )}
    </div>
  )
}

BillingRunOverride.propTypes = {
  error: PropType.string,
  value: PropType.object,
  onChange: PropType.func,
  classes: PropType.object
}
export default BillingRunOverride
